import { Injectable } from '@angular/core';
import { ModalSampleComponent } from '@app/my-booking/sub-components/modal-sample/modal-sample.component';
import { PassengerInformationInputModalFooterComponent } from '@app/my-booking/sub-components/passenger-information-input-modal/passenger-information-input-modal-footer.component';
import { PassengerInformationInputModalHeaderComponent } from '@app/my-booking/sub-components/passenger-information-input-modal/passenger-information-input-modal-header.component';
import { PassengerInformationInputModalComponent } from '@app/my-booking/sub-components/passenger-information-input-modal/passenger-information-input-modal.component';
import { DATA_CODE } from '@common/helpers/constants/my-booking/my_booking_passenger.contant';
import { PassengerInformationInputModalEditArea } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MListData } from '@common/interfaces';
import { AirportI18n, AirportI18nAll } from '@common/interfaces/master/airport_i18n_all';
import { MMileageProgramI18n } from '@common/interfaces/master/m_mileage_program_i18n';
import { MasterDataPassenger } from '@common/interfaces/my-booking-passenger/my-booking-passenger.interface';
import { ModalType } from '@lib/components';
import { SupportClass } from '@lib/components/support-class';
import { AswMasterService, CommonLibService, ModalIdParts, ModalService } from '@lib/services';
import { BehaviorSubject, Subscription } from 'rxjs';
import { PassengerInformationInputModalStoreService } from '../passenger-information-input-modal/passenger-information-input-modal-store.service';
import { CountryI18nBy2LetterCodes } from '@common/interfaces/master/country_i18n_by2lettercode';
import {
  FrequentFlyerCard,
  GetOrderResponseDataTasks,
  Traveler,
  TravelerRegulatoryDetailsVisasInner,
  Type1,
} from 'src/sdk-servicing';
import { CountryCountryI18nAllData } from '@common/master';
import { parseEmptyArray, stringEmptyDefault } from '@common/helpers';

@Injectable()
export class MyBookingPassengerService extends SupportClass {
  private _subscriptions: Subscription = new Subscription();
  transitionSourceFullFunctionPageId = '';
  _modalIdParts: ModalIdParts;

  private masterDataPassenger$ = new BehaviorSubject<MasterDataPassenger>({});
  masterDataPassengerAction$ = this.masterDataPassenger$.asObservable();

  constructor(
    private _aswMasterService: AswMasterService,
    private _common: CommonLibService,
    private _storeService: PassengerInformationInputModalStoreService,
    private _modalService: ModalService
  ) {
    super();
    this._modalIdParts = this._modalService.defaultIdPart(ModalSampleComponent);
  }

  init() {}

  updateMasterDataPassenger(data: MasterDataPassenger) {
    this.masterDataPassenger$.next(data);
  }

  /**
   * (1.4.1) 以下の内容でASWDB(マスタ)より電話番号種別を取得する。
   */
  getTypeInfoFromMLISTDATA(typePhone: string, _mListData?: MListData[]): string {
    const _lang = this._common.aswContextStoreService.aswContextData.lang;
    const itemInfo = _mListData?.find(
      (item: MListData) => item?.data_code === DATA_CODE.PD_010 && item?.lang === _lang && item?.value === typePhone
    );
    return itemInfo?.display_content ?? '';
  }

  // (1.4.2) 以下の内容でASWDB(マスタ)よりマイレージプログラム名称を取得する。
  getMileageProgramFromMileageProgramI18N(
    frequentFlyerCard: FrequentFlyerCard,
    _mMileageProgramI18N: MMileageProgramI18n[]
  ) {
    const _lang = this._common.aswContextStoreService.aswContextData.lang;
    const itemInfo = _mMileageProgramI18N?.find(
      (item: MMileageProgramI18n) => item?.lang === _lang && item?.carrier_code === frequentFlyerCard?.companyCode
    );
    return itemInfo;
  }

  // (1.4.4) 以下の内容でASWDB(マスタ)より空港名称を取得する。
  getAirportFromMAirport(locationCode?: string, mAirport18data?: AirportI18n) {
    return mAirport18data?.[String(locationCode)]?.[0].airport_name;
  }

  /**
   * (1.4.5) 以下の内容でASWDB(マスタ)より国コード(3レター)を取得する。
   */
  getCountry3LetterCodeFromMCountry(issuanceCountryCode?: string, _mCountry?: CountryI18nBy2LetterCodes) {
    return _mCountry?.[String(issuanceCountryCode)]?.[0].country_3letter_code ?? '';
  }

  /**
   * (1.4.6) 以下の内容でASWDB(マスタ)より国名称を取得する。
   */
  getCountryNameFromMCountryI18N(country3LetterCode?: string, _mCountryI18N?: Array<CountryCountryI18nAllData>) {
    return parseEmptyArray(_mCountryI18N as CountryCountryI18nAllData[]).find(
      (data) => data.country_3letter_code === country3LetterCode
    )?.country_name;
  }

  /**
   * [11.4] 滞在資格情報(US)
   */
  getListVisaOrResidentAlienCardRegistrationRequired(flights: Type1[], tasks?: GetOrderResponseDataTasks) {
    const lstFlightIdHasVisaOrResidentAilenCard = new Set<string>();

    flights?.forEach((flight) => {
      if (
        // 11.5.1.2  PNR情報取得API応答結果.data.task.<当該セグメント.id>.isVisaOrResidentAlienCardRegistrationRequired = true
        flight?.id &&
        tasks?.[flight?.id]?.isVisaOrResidentAlienCardRegistrationRequired
      ) {
        lstFlightIdHasVisaOrResidentAilenCard.add(flight?.id);
      }
    });

    return [...lstFlightIdHasVisaOrResidentAilenCard];
  }

  // [11.5] 滞在資格情報(US以外)
  getListVisaInformation(
    flights: Type1[],
    traveler: Traveler,
    tasks?: GetOrderResponseDataTasks,
    _mCountry?: CountryI18nBy2LetterCodes
  ) {
    // 空の国２レターコードリストを作成する。
    const seenCountry2LetterCodeList = new Set<string>();
    // 1.以下のすべての条件を満たす場合、国２レターコードリストに当該セグメント.arrival.country2letterCodeを追加する。
    // （当該セグメント.arrival.country2letterCodeが国２レターコードリストに存在した場合、国２レターコードリストに追加しない）。
    flights?.forEach((segment) => {
      // 1 以下のすべての条件を満たす場合、visa情報を取得する。
      if (
        // 1.1 当該セグメント.isJapanDomesticFlight = false
        segment.isJapanDomesticFlight === false &&
        // かつ当該セグメント.arrival.country2letterCodeと当該セグメント.departure.country2letterCodeが異なる
        segment?.arrival?.country2letterCode !== segment?.departure?.country2letterCode &&
        // 1.2.PNR情報取得API応答.data.tasks.＜当該セグメント＞.isVisaOrResidentAlienCardRegistrationRequired = false、またはが存在しない
        tasks?.[segment?.id!]?.isVisaOrResidentAlienCardRegistrationRequired !== true
      ) {
        segment?.arrival?.country2letterCode && seenCountry2LetterCodeList.add(segment.arrival.country2letterCode);
      }
    });

    // 2.以下の通りに、国コードリストを取得する。
    // 2.1 空の国３レターコードリストを作成する。
    const seenCountry3LetterCodeList: string[] = [];
    // <以下、国２レターコードリストの要素数繰り返し>（それぞれの該当国２レターコード）
    seenCountry2LetterCodeList.forEach((code) => {
      // 2.2 該当国２レターコードを「イベントID(1)」処理によって、国コード3レターを取得する,
      const country3LetterCode = this.getCountry3LetterCodeFromMCountry(code, _mCountry);
      // 2.3国３文字コードリストに国３レターコードを追加する。
      seenCountry3LetterCodeList.push(country3LetterCode);
    });

    // 2.4 国２レターコードリストと国３レターコードリストを組み合わせて、国コードリストとして保持する。
    const seenCountry2And3LetterCode = [...seenCountry2LetterCodeList, ...seenCountry3LetterCodeList];

    // 3.visa情報リストを取得する。
    // 3.1 空のvisa情報リストを作成する。
    const visasInformation: TravelerRegulatoryDetailsVisasInner[] = [];
    const visas = parseEmptyArray(traveler?.regulatoryDetails?.visas);

    // 3.2 <以下、当該入国関連情報.visasの要素数繰り返し>（それぞれの要素が該当visa情報）
    visas.forEach((visa) => {
      const _visaItem = { ...visa };
      const issuanceCountryCode: string = stringEmptyDefault(_visaItem?.issuanceCountryCode);
      // 該当visa情報.issuanceCountryCodeが国コードリストに存在する場合、visa情報リストに 該当visa情報を追加する。
      if (seenCountry2And3LetterCode.includes(issuanceCountryCode)) {
        visasInformation.push(_visaItem);
      }
    });

    return visasInformation;
  }

  handleEvents(editArea: PassengerInformationInputModalEditArea, travelerId: string) {
    // (2.1) 搭乗者情報入力へ連携するパラメータとして、以下の項目に値を設定し、保持する。
    this._storeService.updateEditArea(editArea);
    this._storeService.updateTravelerId(travelerId);
    // (2.2) モーダル共通処理(G01-011)を呼出し、搭乗者情報入力(S04-M010)モーダルを開く。
    this._modalIdParts = {
      id: 'S04-M010',
      closeBackEnable: true,
      type: ModalType.TYPE8,
      content: PassengerInformationInputModalComponent,
      header: PassengerInformationInputModalHeaderComponent,
      footer: PassengerInformationInputModalFooterComponent,
    };
    this._modalService.showSubPageModal(this._modalIdParts);
  }

  handleEventsWithPayload(editArea: PassengerInformationInputModalEditArea, travelerId: string, payload: any) {
    // (2.1) 搭乗者情報入力へ連携するパラメータとして、以下の項目に値を設定し、保持する。
    this._storeService.updateEditArea(editArea);
    this._storeService.updateTravelerId(travelerId);
    // (2.2) モーダル共通処理(G01-011)を呼出し、搭乗者情報入力(S04-M010)モーダルを開く。
    this._modalIdParts = {
      id: 'S04-M010',
      closeBackEnable: true,
      type: ModalType.TYPE8,
      content: PassengerInformationInputModalComponent,
      header: PassengerInformationInputModalHeaderComponent,
      footer: PassengerInformationInputModalFooterComponent,
      payload: payload,
    };
    this._modalService.showSubPageModal(this._modalIdParts);
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
